import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useCrew } from '../hooks/useCrew';
import { useProfileContext } from '../hooks/useProfileContext';
import { useConfig } from '../hooks/useConfig';
import { useRackUsage } from '../hooks/useRackUsage';
import { useEntityEditor, entityNameUnique } from '../hooks/useEntityEditor';
import { bandUrlBase, nameToSlug } from '../utils/slug';
import Button from '../components/ui/Button';
import PageHeader from '../components/PageHeader';
import CollapsibleSection from '../components/CollapsibleSection';
import BacklineEditor from '../components/BacklineEditor';
import AutoTextarea from '../components/AutoTextarea';
import InstrumentEditor from '../components/InstrumentEditor';
import MonitoringSection from '../components/MonitoringSection';
import { detectInstrumentConflict, formatConflictMessage } from '../lib/instrumentConflict';
import { createBacklineItem, createInstrument, createCrewMember, mergeStageRacks } from '../types';
import type { CrewMember, CrewRole, Instrument, DietType, TransportType } from '../types';
import NumberPicker from '../components/NumberPicker';

// Crew tags, one member can hold several per band (replaces the old single
// `Role` select). 'manager' = the band's Group Manager (legacy isManager).
// NB: the 'crew' tag is intentionally ABSENT — being crew is already carried by
// the card living in rider_crew (category crew), so a redundant 'crew' tag was
// removed (2026-08-23).
const TAG_OPTIONS: CrewRole[] = [
  'driver', 'foh', 'guest', 'lights', 'manager', 'mon', 'other', 'stagehand', 'tour manager', 'production manager', 'video', 'guitar tech', 'drum tech', 'keys tech',
];

export default function CrewEditPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const isNew = id === 'new' || window.location.pathname.endsWith('/crew/new');
  const navigate = useNavigate();
  const { activeProfileId, getProfile } = useProfileContext();
  const activeProfile = activeProfileId ? getProfile(activeProfileId) : null;
  const bandSlug = activeProfile?.slug || activeProfileId || '';
  const base = activeProfileId ? bandUrlBase(bandSlug, activeProfile?.name) : '';
  const { crew, updateCrew, saveCrewToServer } = useCrew();
  const { config } = useConfig();
  const racks = useMemo(
    () => mergeStageRacks(config.globalRacks, config.specs, (config.rider?.transport as TransportType) || 'van'),
    [config.globalRacks, config.specs, config.rider?.transport],
  );
  const { usage } = useRackUsage({ crew });

  // PATCH MARK favourites (2026-10-23): colours used on any track of any card
  // of the band (musicians + crew) — clickable swatches next to each picker.
  const markFavourites = useMemo(() => {
    const set = new Set<string>();
    const scan = (list: { instruments?: { tracks?: { patchMark?: string }[] }[]; iemPatchMark?: string; wedgePatchMark?: string; subPatchMark?: string }[]) => {
      for (const p of list) {
        for (const inst of p.instruments || []) for (const tr of inst.tracks || []) {
          if (typeof tr.patchMark === 'string' && tr.patchMark) set.add(tr.patchMark);
        }
        // Monitor-return marks join the favourites pool too (v0.1.63, user).
        for (const k of ['iemPatchMark', 'wedgePatchMark', 'subPatchMark'] as const) {
          const c = (p as any)[k];
          if (typeof c === 'string' && c) set.add(c);
        }
      }
    };
    scan(crew as never);
    return [...set];
  }, [crew]);


  const [member, setMemberRaw] = useState<CrewMember | null>(null);

  // Shared-INHERITANCE (2026-10-23, user): for the instrument being edited, does
  // ANOTHER person's same-named instrument carry a ticked sharedWithReplacement
  // track? The replacement's checkbox then shows the inherited state (checked +
  // disabled + tooltip naming the main player) instead of a confusing free tick.
  const inheritedSharedFor = useCallback((inst: { name?: string }) => {
    const name = (inst.name || '').trim().toLowerCase();
    if (!name) return null;
    for (const m of crew) {
      if (member && m.id === member.id) continue;
      for (const other of m.instruments || []) {
        if ((other.name || '').trim().toLowerCase() !== name) continue;
        for (const tr of other.tracks || []) {
          if (tr.sharedWithReplacement) return { ticked: true, by: m.name || 'Unnamed' };
        }
      }
    }
    return null;
  }, [crew, member]);

  // Wrapper: any edit to the local `member` also propagates to the `crew`
  // array so the hook's debounced auto-save persists it.
  const setMember = useCallback((updater: CrewMember | null | ((prev: CrewMember | null) => CrewMember | null)) => {
    setMemberRaw(prev => {
      const next =
        typeof updater === 'function' ? (updater as (p: CrewMember | null) => CrewMember | null)(prev) : updater;
      if (next && !isNew) updateCrew(next.id, next);
      return next;
    });
  }, [isNew, updateCrew]);

  // Shared editor behaviour (diet write-through to Team, cross-referenced
  // person views, Save guard/saving flag) — the identical half of the two
  // rider-card editors. The card state + handlers stay page-owned.
  const {
    saving,
    dietValue,
    dietNotesValue,
    nonFoodAllergiesValue,
    dietTypes,
    updateDiet,
    personInstruments,
    personTags,
    handleSave,
  } = useEntityEditor({
    // 'team' here is INTENTIONAL (matches the pre-extraction Crew page): the
    // banner text is part of the observable behaviour and must not change.
    scope: 'team',
    entity: member,
    setEntity: setMember,
    validate: (m, tr) => {
      const m2 = m as CrewMember;
      if (!m2.name.trim() && !(m2.tags || []).length) return tr('crewEdit.enterNameOrTag');
      const displayName = m2.name || m2.tags?.[0] || m2.role;
      if (!entityNameUnique(displayName, crew as unknown as Array<{ id: string; name?: string; role?: string }>, m2.id)) return tr('crewEdit.nameExists');
      return null;
    },
    failedSave: t('crewEdit.failedSave'),
    onSave: async () => {
      const m = member;
      if (!m) return;
      let updated: CrewMember[];
      if (isNew) {
        updated = [...crew, m];
      } else {
        updated = crew.map(c => c.id === m.id ? m : c);
      }
      if (!isNew) {
        updateCrew(m.id, m);
      }

      // Force save to server before navigating.
      if (activeProfileId) {
        await saveCrewToServer(updated);
      }

      navigate(`${base}/crew`);
    },
    t,
  });

  useEffect(() => {
    const routeIsNew = isNew || window.location.pathname.endsWith('/crew/new');
    if (routeIsNew) {
      setMember(createCrewMember());
    } else if (id) {
      let existing = crew.find(c => c.id === id);
      if (!existing) {
        existing = crew.find(c => nameToSlug(c.name) === id || nameToSlug(c.role) === id);
      }
      if (existing) setMemberRaw(existing);
    }
  }, [id, isNew, crew]);

  const handleChange = useCallback((field: keyof CrewMember, value: any) => {
    setMember(prev => prev ? { ...prev, [field]: value } : prev);
  }, []);

  const handleAddBackline = useCallback(() => {
    if (!member) return;
    handleChange('backline', [...(member.backline || []), createBacklineItem()]);
  }, [member, handleChange]);

  // --- Tags (multi per band) ---
  // The first selected tag is mirrored into `role` so older consumers
  // (list cards, PDF, CrewRole-based lookups) keep working unchanged.
  const handleToggleTag = useCallback((tag: string) => {
    if (!member) return;
    // Seed the toggling from the real tags only, and ALWAYS strip any
    // category tag ('crew'/'musician') that may linger on a legacy card — a
    // rider_crew card carries its category implicitly (the table it lives in),
    // so 'crew' must never be a tag (else a crew with tags ['foh'] shows as
    // "crew, foh"). Also never fall back to member.role when it is a category.
    const CATEGORY_ROLE = new Set(['crew', 'musician']);
    const base =
      member.tags && member.tags.length
        ? member.tags
        : member.role && !CATEGORY_ROLE.has(member.role)
          ? [member.role]
          : [];
    const cur = base.filter((x) => !CATEGORY_ROLE.has(x));
    const next = cur.includes(tag) ? cur.filter(t => t !== tag) : [...cur, tag];
    setMember(prev => prev ? { ...prev, tags: next, role: (next[0] || '') as CrewRole } : prev);
  }, [member, setMember]);

  // --- Tag libre (2026-11-03) : « production manager » est au catalogue, mais un
  // groupe peut en inventer d'autres (« backline tech CI », « truck driver »…).
  // Normalisation : trim + espaces compactés, pas de virgule (le tag est joint dans
  // une liste), doublon ignoré, et le tag devient le `role` s'il n'y en a pas.
  const [newTag, setNewTag] = useState('');
  const handleAddCustomTag = useCallback(() => {
    if (!member) return;
    const tag = newTag.trim().replace(/\s+/g, ' ').replace(/,/g, ' ');
    if (!tag) return;
    const CATEGORY_ROLE = new Set(['crew', 'musician']);
    const base = member.tags && member.tags.length ? member.tags : (member.role && !CATEGORY_ROLE.has(member.role) ? [member.role] : []);
    const cur = base.filter((x) => !CATEGORY_ROLE.has(x));
    if (cur.some((x) => x.toLowerCase() === tag.toLowerCase())) { setNewTag(''); return; }
    const next = [...cur, tag];
    setMember(prev => prev ? { ...prev, tags: next, role: (next[0] || '') as CrewRole } : prev);
    setNewTag('');
  }, [member, newTag, setMember]);

  // --- Instruments (a crew member can also play) ---
  const handleAddInstrument = useCallback(() => {
    if (!member) return;
    handleChange('instruments', [...(member.instruments || []), createInstrument()]);
  }, [member, handleChange]);

  const handleUpdateInstrument = useCallback((updated: Instrument) => {
    if (!member) return;
    handleChange('instruments', (member.instruments || []).map(inst =>
      inst.id === updated.id ? updated : inst
    ));
  }, [member, handleChange]);

  const handleMoveInstrument = useCallback((instrumentId: string, dir: -1 | 1) => {
    if (!member) return;
    const arr = [...(member.instruments || [])];
    const idx = arr.findIndex(inst => inst.id === instrumentId);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    handleChange('instruments', arr);
  }, [member, handleChange]);

  const handleRemoveInstrument = useCallback((instrumentId: string) => {
    if (!member) return;
    handleChange('instruments', (member.instruments || []).filter(inst => inst.id !== instrumentId));
  }, [member, handleChange]);

  if (!member) {
    return (
      <div className="page" style={{ padding: '48px', textAlign: 'center' }}>
        <p style={{ color: 'var(--text-dim)' }}>{t('crewEdit.loading')}</p>
        {!isNew && (
          <button className="btn btn-secondary" onClick={() => navigate(`${base}/crew`)}>
            {t('crewEdit.backToCrew')}
          </button>
        )}
      </div>
    );
  }

    return (
      <div className="page musician-edit-page" data-edit-page="crew" data-density="socle">
      <PageHeader
        title={isNew ? t('crewEdit.newCrew') : t('crewEdit.editTitle', { name: member.name || member.role || t('crewEdit.unnamed') })}
        actions={
          <>
            <Button variant="secondary" data-edit-back onClick={() => navigate(`${base}/crew`)}>
              {t('crewEdit.backToList')}
            </Button>
            {isNew && (
              <Button
                variant="primary"
                data-edit-save
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? t('crewEdit.saving') : t('crewEdit.create')}
              </Button>
            )}
          </>
        }
      />

      <div className="musician-form">
        <CollapsibleSection title={t('crewEdit.crewInfo')}>
          <div className="form-row rc-form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>{t('crewEdit.name')}</label>
              <input
                type="text"
                data-edit-field="name"
                value={member.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={t('crewEdit.namePh')}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label>{t('crewEdit.baseRole')}</label>
            <select
              value={member.baseRole || 'main'}
              onChange={(e) => handleChange('baseRole', e.target.value as 'main' | 'session')}
              className="form-input"
            >
              <option value="main">{t('crewEdit.mainPermanent')}</option>
              <option value="session">{t('crewEdit.sessionDeputy')}</option>
            </select>
            <small style={{ color: 'var(--text-dim)' }}>{t('crewEdit.baseRoleHint')}</small>
          </div>

          <div className="form-group">
            <label>{t('crewEdit.tags')}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {/* Tags du catalogue + tags LIBRES déjà enregistrés (2026-11-03) : un tag
                  saisi à la main doit rester visible et supprimable, sinon il devient
                  invisible dans l'UI tout en restant en base. */}
              {[...TAG_OPTIONS, ...(member.tags || []).filter((tg) => !TAG_OPTIONS.includes(tg as CrewRole))].map((tag) => {
                const current = member.tags && member.tags.length ? member.tags : (member.role ? [member.role] : []);
                const active = current.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    className={`rc-pill${active ? ' on' : ''}`}
                    onClick={() => handleToggleTag(tag as CrewRole)}
                    title={TAG_OPTIONS.includes(tag as CrewRole) ? undefined : t('crewEdit.customTag')}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <input
                className="form-input"
                value={newTag}
                maxLength={40}
                placeholder={t('crewEdit.addTagPh')}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); handleAddCustomTag(); }
                }}
                data-testid="crew-custom-tag-input"
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleAddCustomTag}
                disabled={!newTag.trim()}
                data-testid="crew-custom-tag-add"
              >
                {t('crewEdit.addTag')}
              </button>
            </div>
            <small style={{ color: 'var(--text-dim)' }}>
              {t('crewEdit.tagsHint')}
            </small>
          </div>

          <div className="form-group">
            <label>{t('crewEdit.color')}</label>
            <input
              type="color"
              className="form-input"
              value={member.color}
              onChange={e => handleChange('color', e.target.value)}
            />
          </div>
        </CollapsibleSection>

        {/* Hospitality per-person (towels & water are person-linked so the show
            rider totals follow the SELECTED people — 2026-08-07) */}
        <CollapsibleSection title={t('crewEdit.hospitality')}>
          {/* Diet first — the dietary requirement is the headline for this person. */}
          <div className="form-grid" style={{ marginBottom: 14 }}>
            <div className="form-group">
              <label>{t('crewEdit.diet')}</label>
              <input
                className="form-input"
                list="diet-types-suggestions"
                value={dietValue}
                onChange={(e) => updateDiet({ diet: e.target.value as DietType })}
                placeholder={t('crewEdit.dietPh')}
              />
              <small style={{ color: 'var(--text-dim)' }}>{t('crewEdit.dietStoredTeam')}</small>
            </div>
            <div className="form-group">
              <label>{t('crewEdit.dietDetails')}</label>
              <AutoTextarea
                value={dietNotesValue}
                onChange={(e) => updateDiet({ dietNotes: e.target.value })}
                placeholder={t('crewEdit.dietDetailsPh')}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>{t('crewEdit.nonFoodAllergies')}</label>
              <AutoTextarea
                value={nonFoodAllergiesValue}
                onChange={(e) => updateDiet({ nonFoodAllergies: e.target.value })}
                placeholder={t('crewEdit.nonFoodAllergiesPh')}
                className="form-input"
              />
              <small style={{ color: 'var(--text-dim)' }}>{t('crewEdit.dietStoredTeam')}</small>
            </div>
          </div>
          {/* Towels & water on the row below — these are person-linked so the show
              rider totals follow the SELECTED people (2026-08-07). */}
          <div className="form-grid">
            <div className="form-group">
              <label>{t('crewEdit.stageTowels')}</label>
              <NumberPicker
                value={member.towels ?? 0}
                options={[0, 1, 2, 3, 4, 5, 6, 8]}
                onChange={(v) => handleChange('towels', v)}
                className="form-input"
              />
              <small style={{ color: 'var(--text-dim)' }}>{t('crewEdit.blackTowels')}</small>
            </div>
            <div className="form-group">
              <label>{t('crewEdit.dressingTowels')}</label>
              <NumberPicker
                value={member.dressingRoomTowels ?? 1}
                options={[0, 1, 2, 3, 4, 5, 6, 8]}
                onChange={(v) => handleChange('dressingRoomTowels', v)}
                className="form-input"
              />
              <small style={{ color: 'var(--text-dim)' }}>{t('crewEdit.dressingTowelsHint')}</small>
            </div>
            <div className="form-group">
              <label>{t('crewEdit.waterBottles')}</label>
              <NumberPicker
                value={member.water ?? 0}
                options={[0, 1, 2, 3, 4, 5, 6, 8, 10, 12]}
                onChange={(v) => handleChange('water', v)}
                className="form-input"
              />
              <small style={{ color: 'var(--text-dim)' }}>{t('crewEdit.waterHint')}</small>
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection title={t('crewEdit.monitoring')}>
          <MonitoringSection
            iemType={member.iemType}
            iemWired={member.iemWired}
            iemWiredProvidedBy={member.iemWiredProvidedBy}
            wedges={member.wedges}
            subwoofer={member.subwoofer || false}
            iemRackId={member.iemRackId}
            iemOutStart={member.iemOutStart}
            wedgeRackId={member.wedgeRackId}
            wedgeOutStart={member.wedgeOutStart}
            wedgesStereo={member.wedgesStereo}
            subRackId={member.subRackId}
            subOutStart={member.subOutStart}
            iemPatchMark={(member as any).iemPatchMark}
            wedgePatchMark={(member as any).wedgePatchMark}
            subPatchMark={(member as any).subPatchMark}
            personMarks={markFavourites}
            racks={racks}
                        usage={usage}
                        editedInstruments={new Set((member.instruments || []).map((i) => i.name).filter(Boolean))}
                        personInstruments={personInstruments}
                        editedTags={new Set(member.tags || [])}
                        personTags={personTags}
                        personLabel={member.name || 'Unnamed'}
                                                onChange={(patch) => {
                                      if (patch.iemType !== undefined) handleChange('iemType', patch.iemType);
                                      if (patch.iemWired !== undefined) handleChange('iemWired', patch.iemWired);
              if (patch.wedges !== undefined) handleChange('wedges', patch.wedges);
              if (patch.subwoofer !== undefined) handleChange('subwoofer', patch.subwoofer);
              if (patch.iemRackId !== undefined) handleChange('iemRackId', patch.iemRackId);
              if (patch.iemOutStart !== undefined) handleChange('iemOutStart', patch.iemOutStart);
              if (patch.wedgeRackId !== undefined) handleChange('wedgeRackId', patch.wedgeRackId);
              if (patch.wedgeOutStart !== undefined) handleChange('wedgeOutStart', patch.wedgeOutStart);
              if (patch.wedgesStereo !== undefined) handleChange('wedgesStereo', patch.wedgesStereo);
              if (patch.subRackId !== undefined) handleChange('subRackId', patch.subRackId);
              if (patch.subOutStart !== undefined) handleChange('subOutStart', patch.subOutStart);
              // PATCH MARKS: undefined MEANS "clear" — presence check, not a
              // !== undefined filter (v0.1.67 fix, same as MusicianEditPage).
              if ('iemPatchMark' in patch) handleChange('iemPatchMark' as never, patch.iemPatchMark);
              if ('wedgePatchMark' in patch) handleChange('wedgePatchMark' as never, patch.wedgePatchMark);
              if ('subPatchMark' in patch) handleChange('subPatchMark' as never, patch.subPatchMark);
            }}
          />
        </CollapsibleSection>

        {/* Instruments — a crew member can also play (same editor as musicians) */}
        <CollapsibleSection
          title={t('crewEdit.instruments')}
          count={member.instruments?.length || 0}
          headerActions={
            <Button variant="primary" size="sm" onClick={handleAddInstrument}>
              {t('crewEdit.addInstrument')}
            </Button>
          }
        >
          {(member.instruments || []).length === 0 && (
            <div className="empty-state">
              <p>{t('crewEdit.noInstruments')}</p>
            </div>
          )}
          <div className="instrument-list">
            {(member.instruments || []).map((inst, idx) => {
              const allPersonTracks = (member.instruments || []).flatMap((i) => i.tracks);
              const conflict = detectInstrumentConflict({
                tracks: inst.tracks,
                allPersonTracks,
                racks,
                usage,
                personLabel: member.name || 'Unnamed',
                instrumentName: inst.name,
                personInstruments,
              });
              return (
              <InstrumentEditor
                key={inst.id}
                instrument={inst}
                racks={racks}
                                usage={usage}
                                editedInstruments={new Set((member.instruments || []).map((i) => i.name).filter(Boolean))}
                                personInstruments={personInstruments}
                                editedTags={new Set(member.tags || [])}
                                personTags={personTags}
                                personLabel={member.name || 'Unnamed'}
                                personMarks={markFavourites}
                                inheritedShared={inheritedSharedFor(inst)}
                                hasConflict={!!conflict}
                conflictMessage={conflict ? formatConflictMessage(t, conflict) : undefined}
                index={idx}
                total={(member.instruments || []).length}
                onChange={handleUpdateInstrument}
                onMoveUp={() => handleMoveInstrument(inst.id, -1)}
                onMoveDown={() => handleMoveInstrument(inst.id, 1)}
                onRemove={() => handleRemoveInstrument(inst.id)}
              />
              );
            })}
          </div>
        </CollapsibleSection>

        {/* Backline */}
        <CollapsibleSection
          title={member.name ? t('crewEdit.backlineTitleFor', { name: member.name }) : t('crewEdit.backlineTitle')}
          headerActions={
            <Button variant="primary" size="sm" onClick={handleAddBackline}>
              {t('crewEdit.addBackline')}
            </Button>
          }
        >
          {(member.backline || []).length === 0 && (
            <p className="empty-state">{t('crewEdit.noBackline')}</p>
          )}
          <BacklineEditor
            items={member.backline || []}
            onChange={(items) => handleChange('backline', items)}
          />
          {/* VenueMicStandsTable RETIRÉ du bloc backline (2026-10-17, user) : le backline
              concerne les instruments — les pieds de micro (type, boom, venue-provided)
              vivent UNIQUEMENT dans la section Mic Stands du rider (PDF + page) et dans
              le tableau des pistes qui les définit. */}
<datalist id="diet-types-suggestions">
            {dietTypes.map((d) => <option key={d} value={d} />)}
          </datalist>
        </CollapsibleSection>
      </div>
    </div>
  );
}
